import React, { useState } from 'react';
import { X, Plus, Calendar, Dumbbell, CheckCircle2, AlertCircle } from 'lucide-react';
import { Exercise, ExerciseProgram, ProgramDay, ProgramDayExercise } from '../types';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { apiPut, apiPost } from '../lib/api';

interface AddToProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  exercise: Exercise | null;
  programs: ExerciseProgram[];
  onSuccess: (updatedPrograms: ExerciseProgram[]) => void;
}

export default function AddToProgramModal({
  isOpen,
  onClose,
  exercise,
  programs,
  onSuccess
}: AddToProgramModalProps) {
  const { t, lang } = useLanguage();

  if (!isOpen || !exercise) return null;

  const locEx = exercise;

  const [selectedProgramId, setSelectedProgramId] = useState<string>(
    programs[0]?.id || ''
  );
  const [targetDayOption, setTargetDayOption] = useState<string>('day_1'); // day id or 'new_day'
  const [customDayTitle, setCustomDayTitle] = useState<string>('');
  
  // Custom overrides for sets/reps in program day
  const [sets, setSets] = useState<number>(exercise.sets || 3);
  const [reps, setReps] = useState<string>(exercise.reps || '10');
  const [rest, setRest] = useState<string>(exercise.rest || t('rehabRestDefault', '20 ثانیه'));
  const [duration, setDuration] = useState<number>(exercise.duration || 5);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currentProgram = programs.find(p => p.id === selectedProgramId);
  const programDays = currentProgram?.days || [];

  const handleConfirmAdd = async () => {
    if (!currentProgram) {
      setErrorMsg(t('selectProgramError', 'لطفاً یک برنامه تمرینی را انتخاب کنید.'));
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      const dayExerciseItem: ProgramDayExercise = {
        exerciseId: exercise.id,
        name: exercise.name,
        category: exercise.category,
        description: exercise.description,
        purpose: exercise.purpose,
        targetMuscles: exercise.targetMuscles,
        sets: sets || 3,
        reps: reps || '10',
        rest: rest || t('rehabRestDefault', '20 ثانیه'),
        duration: duration || 5,
        difficulty: exercise.difficulty,
        precautions: exercise.precautions,
        equipment: exercise.equipment,
        image: exercise.image,
        videoUrl: exercise.videoUrl,
        isCustom: exercise.isCustom,
        scope: exercise.scope || (exercise.user_id ? 'personal' : 'global')
      };

      let updatedDays: ProgramDay[] = [...programDays];

      if (targetDayOption === 'new_day' || updatedDays.length === 0) {
        // Create new day in program
        const nextDayNum = updatedDays.length + 1;
        const newDay: ProgramDay = {
          id: `day_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          programId: currentProgram.id,
          dayNumber: nextDayNum,
          title: customDayTitle.trim() || t('dayDefaultTitle', undefined, { day: formatNumberByLang(nextDayNum, lang) }),
          notes: '',
          exercises: [dayExerciseItem]
        };
        updatedDays.push(newDay);
      } else {
        // Append to existing day
        updatedDays = updatedDays.map(d => {
          if (d.id === targetDayOption || (d.dayNumber && `day_${d.dayNumber}` === targetDayOption)) {
            // Check if already in day
            const exists = (d.exercises || []).some(
              e => (e.exerciseId && e.exerciseId === exercise.id) || e.name === exercise.name
            );
            if (exists) {
              return d; // already present
            }
            return {
              ...d,
              exercises: [...(d.exercises || []), dayExerciseItem]
            };
          }
          return d;
        });
      }

      // Persist to D1 via API
      const res = await apiPut(`/api/rehab/programs/${currentProgram.id}`, {
        title: currentProgram.title,
        description: currentProgram.description,
        category: currentProgram.category,
        difficulty: currentProgram.difficulty,
        days: updatedDays
      });

      if (res && res.success) {
        const updatedAllPrograms = programs.map(p => {
          if (p.id === currentProgram.id) {
            return {
              ...p,
              days: updatedDays
            };
          }
          return p;
        });
        onSuccess(updatedAllPrograms);
        onClose();
      } else {
        throw new Error(res?.error || t('dbProgramSaveError', 'خطا در ثبت روز برنامه در دیتابیس D1.'));
      }
    } catch (err: any) {
      setErrorMsg(err?.message || t('dbProgramSaveError', 'خطا در ذخیره‌سازی تغییرات در دیتابیس.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-purple-200 dark:border-purple-900/50 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-purple-600 text-white flex items-center justify-center shadow-xs">
              <Plus className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                {t('addToMyProgramTitle', 'افزودن تمرین به برنامه تمرینی')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs sm:max-w-sm">
                {locEx.name}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {errorMsg && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl text-xs text-red-700 dark:text-red-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {programs.length === 0 ? (
            <div className="p-6 text-center bg-gray-50 dark:bg-gray-800/40 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 space-y-2">
              <p className="text-xs text-gray-600 dark:text-gray-300">
                {t('noMultiDayProgramsWarning', 'هنوز برنامه چندروزه‌ای ایجاد نکرده‌اید. ابتدا در بخش «برنامه‌های چندروزه» یک برنامه بسازید.')}
              </p>
            </div>
          ) : (
            <>
              {/* Select Program */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('selectTargetProgram', 'انتخاب برنامه تمرینی مقصد:')}
                </label>
                <select
                  value={selectedProgramId}
                  onChange={(e) => {
                    setSelectedProgramId(e.target.value);
                    setTargetDayOption('new_day');
                  }}
                  className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                >
                  {programs.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} ({t('rehabDaysCount', undefined, { count: formatNumberByLang(p.days?.length || 0, lang) })})
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Day in Program */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  {t('selectTargetDay', 'انتخاب روز تمرینی:')}
                </label>
                <select
                  value={targetDayOption}
                  onChange={(e) => setTargetDayOption(e.target.value)}
                  className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                >
                  {programDays.map((d) => (
                    <option key={d.id} value={d.id}>
                      {t('dayPrefix', 'روز')} {formatNumberByLang(d.dayNumber, lang)}: {d.title} ({formatNumberByLang(d.exercises?.length || 0, lang)} {t('exercisesCountUnit', 'حرکت')})
                    </option>
                  ))}
                  <option value="new_day">+ {t('addNewProgramDayBtn', 'ایجاد روز تمرینی جدید در این برنامه')}</option>
                </select>
              </div>

              {/* If new day is selected, input day title */}
              {(targetDayOption === 'new_day' || programDays.length === 0) && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    {t('newDayTitleInput', 'عنوان روز جدید:')}
                  </label>
                  <input
                    type="text"
                    value={customDayTitle}
                    onChange={(e) => setCustomDayTitle(e.target.value)}
                    placeholder={t('newDayTitlePlaceholder', undefined, { day: formatNumberByLang(programDays.length + 1, lang) })}
                    className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              )}

              {/* Override Exercise Parameters for this Day */}
              <div className="pt-2 border-t border-gray-100 dark:border-gray-800 space-y-3">
                <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 block">
                  {t('customizeDayParameters', 'تنظیم ست، تکرار و استراحت برای این برنامه:')}
                </span>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">
                      {t('rehabCustomSets', 'تعداد ست')}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={sets}
                      onChange={(e) => setSets(parseInt(e.target.value, 10) || 1)}
                      className="w-full p-2 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">
                      {t('rehabCustomReps', 'تکرار / زمان مکث')}
                    </label>
                    <input
                      type="text"
                      value={reps}
                      onChange={(e) => setReps(e.target.value)}
                      className="w-full p-2 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">
                      {t('rehabCustomRest', 'استراحت')}
                    </label>
                    <input
                      type="text"
                      value={rest}
                      onChange={(e) => setRest(e.target.value)}
                      className="w-full p-2 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] text-gray-600 dark:text-gray-400 mb-1">
                      {t('rehabCustomDuration', 'مدت زمان (دقیقه)')}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={120}
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value, 10) || 1)}
                      className="w-full p-2 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
          >
            {t('cancel', 'انصراف')}
          </button>
          
          <button
            type="button"
            onClick={handleConfirmAdd}
            disabled={loading || programs.length === 0}
            className="px-5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{loading ? t('saving', 'در حال افزودن...') : t('addToProgramConfirm', 'افزودن به برنامه')}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
